<template>
  <div class="smart-grid-page">
    <section class="common-section overview-section">
      <section-title :titleInfo="overviewTitle"></section-title>

      <div class="overview-lead">
        <div class="overview-statement">
          <span class="eyebrow">SMART DISTRIBUTION GRID</span>
          <h2>贯通数据、分析与执行，构建智慧配电业务闭环</h2>
          <p>
            面向规划、运检、调度与运营场景，融合业务平台、网架分析、自动化工具和 AI
            智能体能力，持续提升配电网的精益管控与协同效率。
          </p>
        </div>
        <div class="system-tags">
          <span v-for="tag in systemTraits" :key="tag">{{ tag }}</span>
        </div>
      </div>

      <div class="product-grid">
        <article
          v-for="(item, index) in productLines"
          :key="item.title"
          class="product-card"
          :style="{ '--accent': item.accent }"
        >
          <span class="product-number">0{{ index + 1 }}</span>
          <span class="product-icon"><operation-icon :name="item.icon"></operation-icon></span>
          <h3>{{ item.title }}</h3>
          <p>{{ item.text }}</p>
          <small>{{ item.caption }}</small>
        </article>
      </div>
    </section>

    <section class="common-section agent-section">
      <section-title :titleInfo="agentTitle"></section-title>

      <div class="agent-method">
        <template v-for="(item, index) in agentMethod">
          <div :key="item" class="method-pill">{{ item }}</div>
          <span v-if="index < agentMethod.length - 1" :key="item + '-plus'">+</span>
        </template>
      </div>

      <div class="agent-showcase">
        <div class="agent-tabs" role="tablist" aria-label="X-Mate 智能体能力">
          <button
            v-for="(item, index) in agentFeatures"
            :key="item.title"
            type="button"
            :class="{ active: activeAgentIndex === index }"
            @click="activeAgentIndex = index"
          >
            <span><operation-icon :name="item.icon"></operation-icon></span>
            <div>
              <strong>{{ item.title }}</strong>
              <small>{{ item.text }}</small>
            </div>
          </button>
        </div>

        <div class="agent-screen">
          <div class="screen-toolbar">
            <span></span><span></span><span></span>
            <strong>{{ activeAgentFeature.title }}</strong>
          </div>
          <transition name="screen-fade" mode="out-in">
            <img
              :key="activeAgentFeature.title"
              :src="activeAgentFeature.image"
              :alt="activeAgentFeature.title + '产品界面'"
            />
          </transition>
        </div>
      </div>

      <div class="cycle-flow">
        <article v-for="(item, index) in cycleSteps" :key="item.title">
          <span>0{{ index + 1 }}</span>
          <strong>{{ item.title }}</strong>
          <small>{{ item.text }}</small>
        </article>
      </div>
    </section>

    <section class="common-section platform-section">
      <section-title :titleInfo="platformTitle"></section-title>

      <div class="platform-card">
        <div class="platform-visual">
          <img :src="platformImage" alt="配网业务综合管控平台界面" />
          <span>配网业务综合管控平台</span>
        </div>
        <div class="platform-copy">
          <span class="section-kicker">全域数据贯通</span>
          <h2>打通多系统壁垒，统一配网业务模型</h2>
          <p>
            围绕电网企业信息化蓝图，贯通设备、运行、检修、故障抢修、工程与优质服务，
            综合利用配电网业务数据，支撑业务精益管控。
          </p>
          <div class="system-chip-list">
            <span v-for="item in platformSystems" :key="item">{{ item }}</span>
          </div>
          <div class="platform-ability-grid">
            <div v-for="item in platformAbilities" :key="item.title">
              <span><operation-icon :name="item.icon"></operation-icon></span>
              <strong>{{ item.title }}</strong>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="common-section research-section">
      <section-title :titleInfo="researchTitle"></section-title>

      <div class="research-grid">
        <article v-for="item in researchItems" :key="item.title">
          <div class="research-image"><img :src="item.image" :alt="item.title" /></div>
          <div class="research-copy">
            <span><operation-icon :name="item.icon"></operation-icon></span>
            <div><h3>{{ item.title }}</h3><p>{{ item.text }}</p></div>
          </div>
        </article>
      </div>

      <div class="research-summary">
        <div>
          <span class="section-kicker">技术研究能力</span>
          <h2>面向新型配电系统的共性分析与动态模拟</h2>
          <p>
            融合多源基础数据，开展配电网理论计算、实用化计算、分布式光伏接入分析和
            Web 化多场景动态模拟，为网架项目评审与规划决策提供技术支撑。
          </p>
        </div>
        <div class="service-scenes">
          <span v-for="item in researchScenes" :key="item">{{ item }}</span>
        </div>
      </div>
    </section>

    <section class="common-section automation-section">
      <section-title :titleInfo="automationTitle"></section-title>

      <div class="automation-card">
        <div class="automation-copy">
          <span class="section-kicker">RPA + 数据质量治理</span>
          <h2>让重复的数据稽查与分析工作自动运行</h2>
          <p>
            集成多源业务数据，持续监控完整性、一致性和准确性，自动识别异常、重复和错误条目，
            并根据业务规则执行定制化稽查任务。
          </p>
          <div class="metric-grid">
            <article v-for="item in automationMetrics" :key="item.value">
              <strong>{{ item.value }}</strong>
              <span>{{ item.label }}</span>
            </article>
          </div>
          <small class="metric-note">* 以上为 PPT 所列项目实践数据，实际效果以具体项目为准。</small>
        </div>
        <div class="automation-visual">
          <img :src="automationImage" alt="配电自动化分析应用界面" />
        </div>
      </div>
    </section>

    <section class="common-section ai-section">
      <section-title :titleInfo="aiTitle"></section-title>

      <div class="ai-tech-grid">
        <article v-for="item in aiTechnologies" :key="item.title">
          <span><operation-icon :name="item.icon"></operation-icon></span>
          <div><h3>{{ item.title }}</h3><p>{{ item.text }}</p></div>
        </article>
      </div>

      <div class="ai-application-card">
        <div>
          <span class="section-kicker">围绕业务链，更加关心人</span>
          <h2>让智能体承接重复工作，让人员聚焦管理优化</h2>
          <p>以可追溯的知识、可编排的技能和电力业务规则，支撑日常运营工作智能化运行。</p>
        </div>
        <div class="ai-scenes">
          <article v-for="item in aiApplications" :key="item.title">
            <span><operation-icon :name="item.icon"></operation-icon></span>
            <strong>{{ item.title }}</strong>
            <small>{{ item.text }}</small>
          </article>
        </div>
      </div>
    </section>

    <section class="common-section value-section">
      <section-title :titleInfo="valueTitle"></section-title>
      <div class="value-grid">
        <article v-for="item in values" :key="item.title">
          <span><operation-icon :name="item.icon"></operation-icon></span>
          <div><h3>{{ item.title }}</h3><p>{{ item.text }}</p></div>
        </article>
      </div>
    </section>

    <section class="contact-banner">
      <div>
        <h2>携手智融科技，共建智慧配电新生态</h2>
        <p>贯通数据、沉淀能力、协同业务，让配网运营更智慧、更高效</p>
      </div>
      <a href="tel:02583585700"><span>立即咨询</span><strong>025-83585700</strong></a>
    </section>
  </div>
</template>

<script>
import SectionTitle from "../../components/section-title.vue";
import OperationIcon from "../../zhiHuiYunWei/cpns/operation-icon.vue";

export default {
  name: "ElecNet",
  components: { SectionTitle, OperationIcon },
  data() {
    return {
      activeAgentIndex: 0,
      overviewTitle: {
        bigtext1: "智慧配电网产品体系",
        smallText: "以业务场景为牵引，形成平台、研究、工具与 AI 智能体协同发展的产品体系",
      },
      agentTitle: {
        bigtext1: "X-Mate 智能体平台",
        smallText: "扎根配网业务场景，把组织经验转化为可复制、可调度的数字化岗位分身",
      },
      platformTitle: {
        bigtext1: "配网业务综合管控",
        smallText: "立足顶层设计，贯通多源系统，支撑配网业务精益管控",
      },
      researchTitle: {
        bigtext1: "网架分析与技术研究",
        smallText: "覆盖网架成图、指标计算、问题诊断和规划推演，服务规划、运检与调度业务",
      },
      automationTitle: {
        bigtext1: "自动化分析应用",
        smallText: "以机器人流程自动化承接重复工作，持续提升数据质量与工作效率",
      },
      aiTitle: {
        bigtext1: "AI 电力业务垂直应用",
        smallText: "知识检索、自然语言查询与业务规则协同，构建可信、可用的电力业务智能体",
        color: true,
      },
      valueTitle: {
        bigtext1: "产品体系协同价值",
        smallText: "以统一数据底座和核心技术能力，推动配网业务协同、提质与增效",
      },
      systemTraits: ["清洁低碳", "安全可控", "灵活高效", "智能友好", "开放互动"],
      productLines: [
        {
          title: "智能体平台",
          text: "角色定位、知识绑定、技能编排，打造数字化岗位分身。",
          caption: "X-Mate AI",
          icon: "aggregate",
          accent: "#16bca9",
        },
        {
          title: "配电网网架产品",
          text: "网架成图、接线识别、指标计算与多场景规划推演。",
          caption: "技术研究类",
          icon: "microgrid",
          accent: "#2b72ee",
        },
        {
          title: "配网综合管控平台",
          text: "打通多系统壁垒，实现业务模型统一与全域协同。",
          caption: "平台类",
          icon: "data",
          accent: "#8065ee",
        },
        {
          title: "配电自动化分析",
          text: "基于 RPA 的数据治理、自动稽查和任务持续执行。",
          caption: "工具类",
          icon: "schedule",
          accent: "#ff8a34",
        },
      ],
      agentMethod: ["角色定位", "知识绑定", "技能编排"],
      agentFeatures: [
        {
          title: "知识库管理",
          text: "知识沉淀、检索与共享",
          icon: "data",
          image: require("../images/elecNet/agent-knowledge.webp"),
        },
        {
          title: "技能库与市场",
          text: "技能可编排、组合与复用",
          icon: "aggregate",
          image: require("../images/elecNet/agent-skills.webp"),
        },
        {
          title: "智能体管理",
          text: "构建数字化岗位矩阵",
          icon: "community",
          image: require("../images/elecNet/agent-role.webp"),
        },
        {
          title: "自动化任务",
          text: "定时执行与状态监控",
          icon: "schedule",
          image: require("../images/elecNet/agent-tasks.webp"),
        },
        {
          title: "成果库",
          text: "结果沉淀并持续回流",
          icon: "shield",
          image: require("../images/elecNet/agent-results.webp"),
        },
      ],
      cycleSteps: [
        { title: "业务场景拆解", text: "识别岗位职责" },
        { title: "经验数字化", text: "沉淀知识规则" },
        { title: "技能与智能体", text: "编排岗位能力" },
        { title: "任务持续运行", text: "稳定自动执行" },
        { title: "成果回流", text: "持续优化能力" },
      ],
      platformImage: require("../images/elecNet/platform-dashboard.webp"),
      platformSystems: ["PMS", "EMS", "DMS", "用电采集", "营销", "ERP", "GIS"],
      platformAbilities: [
        { title: "设备管理", icon: "battery" },
        { title: "运行监测", icon: "data" },
        { title: "检修管理", icon: "schedule" },
        { title: "故障抢修", icon: "efficiency" },
        { title: "工程管理", icon: "community" },
        { title: "优质服务", icon: "shield" },
      ],
      researchItems: [
        {
          title: "网架拓扑与成图",
          text: "开展供电单元接线组分析、网架成图与接线类型识别。",
          icon: "microgrid",
          image: require("../images/elecNet/grid-topology.webp"),
        },
        {
          title: "指标计算与诊断",
          text: "面向网架指标开展计算分析，快速定位薄弱环节与问题。",
          icon: "data",
          image: require("../images/elecNet/grid-diagnosis.webp"),
        },
        {
          title: "沙盘模拟与规划",
          text: "支撑多场景动态模拟、项目评审和规划方案辅助决策。",
          icon: "efficiency",
          image: require("../images/elecNet/grid-simulation.webp"),
        },
      ],
      researchScenes: ["规划", "运检", "调度", "新能源接入分析"],
      automationImage: require("../images/elecNet/rpa-analysis.webp"),
      automationMetrics: [
        { value: "2名", label: "减少重复运维投入" },
        { value: "75%", label: "数据治理时间减少" },
        { value: "3倍", label: "日常工作效率提升" },
        { value: "80+万元", label: "项目预算优化" },
      ],
      aiTechnologies: [
        { title: "RAG 技术", text: "知识库检索增强，让回答有据可依", icon: "data" },
        { title: "NL2SQL + LLM", text: "通过自然语言精准查询业务数据", icon: "aggregate" },
        { title: "规则适配", text: "融合电力业务专属逻辑与合规规则", icon: "schedule" },
      ],
      aiApplications: [
        { title: "运营数据查询", text: "多维数据快速检索", icon: "data" },
        { title: "经营报表生成", text: "自动汇总并形成成果", icon: "efficiency" },
        { title: "业务合规审核", text: "规则驱动智能校核", icon: "shield" },
      ],
      values: [
        { title: "统一数据底座", text: "推动数据标准化管理与高效共享", icon: "data" },
        { title: "保障数据质量", text: "确保业务数据准确、完整和一致", icon: "shield" },
        { title: "提供技术支撑", text: "以核心技术能力支撑业务创新", icon: "microgrid" },
        { title: "支撑业务协同", text: "打通流程断点，促进跨部门协作", icon: "aggregate" },
        { title: "提升运营效率", text: "自动化处理缩短业务响应周期", icon: "efficiency" },
        { title: "创新解决方案", text: "构建面向新型电力系统的差异化能力", icon: "green" },
      ],
    };
  },
  computed: {
    activeAgentFeature() {
      return this.agentFeatures[this.activeAgentIndex] || this.agentFeatures[0];
    },
  },
};
</script>

<style lang="scss" scoped>
.smart-grid-page {
  width: 100%;
  overflow: hidden;
  color: #15233a;
  background: #fff;
}

.common-section {
  box-sizing: border-box;
  padding: 100px clamp(32px, 8.33vw, 160px);
}

.smart-grid-page /deep/ .title-small-text {
  width: 100%;
  max-width: 760px;
}

h2,
h3,
p {
  margin: 0;
}

.overview-section {
  background: linear-gradient(180deg, #f6f9ff 0%, #fff 72%);
}

.overview-lead {
  margin-bottom: 32px;
  padding: 34px 38px;
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(360px, 0.8fr);
  align-items: center;
  gap: 50px;
  border: 1px solid #dfe8f5;
  border-radius: 12px;
  background: linear-gradient(110deg, #f2f7ff, #fff 64%);
  box-shadow: 0 16px 42px rgba(26, 78, 151, 0.08);
}

.eyebrow,
.section-kicker {
  display: block;
  color: #2164e8;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.5px;
}

.overview-statement h2 {
  max-width: 760px;
  margin: 12px 0 14px;
  color: #15396f;
  font-size: 29px;
  font-weight: 600;
  line-height: 1.35;
}

.overview-statement p {
  max-width: 800px;
  color: #66758d;
  font-size: 15px;
  line-height: 1.85;
}

.system-tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
}

.system-tags span {
  min-width: 96px;
  padding: 12px 18px;
  box-sizing: border-box;
  color: #24549c;
  border: 1px solid #ccdcf3;
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.92);
  text-align: center;
  font-size: 13px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.product-card {
  min-height: 245px;
  padding: 28px;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid #e1e8f2;
  border-radius: 11px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(22, 65, 126, 0.07);
  transition: transform 0.3s, box-shadow 0.3s;
}

.product-card::before {
  content: "";
  width: 100%;
  height: 4px;
  position: absolute;
  top: 0;
  left: 0;
  background: var(--accent);
}

.product-card:hover {
  box-shadow: 0 18px 42px rgba(22, 65, 126, 0.12);
  transform: translateY(-4px);
}

.product-number {
  position: absolute;
  top: 26px;
  right: 26px;
  color: #d9e5f7;
  font-size: 14px;
  letter-spacing: 1px;
}

.product-icon {
  width: 52px;
  height: 52px;
  padding: 11px;
  display: block;
  box-sizing: border-box;
  color: var(--accent);
  border-radius: 10px;
  background: #f1f6ff;
}

.product-card h3 {
  margin: 22px 0 10px;
  font-size: 20px;
  font-weight: 600;
}

.product-card p {
  min-height: 48px;
  color: #6c798d;
  font-size: 13px;
  line-height: 1.7;
}

.product-card small {
  margin-top: 18px;
  display: block;
  color: var(--accent);
  font-size: 12px;
}

.agent-section {
  background: #f6f9fe;
}

.agent-method {
  margin-bottom: 28px;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  border: 1px solid #dce7f5;
  border-radius: 10px;
  background: #fff;
}

.agent-method > span {
  color: #95a4ba;
  font-size: 26px;
  font-weight: 300;
}

.method-pill {
  min-width: 190px;
  padding: 13px 24px;
  box-sizing: border-box;
  color: #1f63ea;
  border: 1px solid #c8dafe;
  border-radius: 30px;
  background: #f5f8ff;
  text-align: center;
  font-size: 17px;
  font-weight: 600;
}

.agent-showcase {
  min-height: 520px;
  padding: 24px;
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 24px;
  box-sizing: border-box;
  border: 1px solid #dfe8f4;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 18px 45px rgba(23, 71, 139, 0.09);
}

.agent-tabs {
  display: grid;
  align-content: center;
  gap: 10px;
}

.agent-tabs button {
  width: 100%;
  min-height: 76px;
  padding: 13px 15px;
  display: flex;
  align-items: center;
  gap: 14px;
  color: #4b5c75;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: all 0.25s;
}

.agent-tabs button > span {
  width: 40px;
  height: 40px;
  padding: 8px;
  flex: none;
  box-sizing: border-box;
  color: #2164e8;
  border-radius: 8px;
  background: #edf4ff;
}

.agent-tabs button strong,
.agent-tabs button small {
  display: block;
}

.agent-tabs button strong {
  margin-bottom: 7px;
  color: #253653;
  font-size: 16px;
  font-weight: 600;
}

.agent-tabs button small {
  color: #8591a3;
  font-size: 12px;
}

.agent-tabs button.active {
  border-color: #9dbfff;
  background: linear-gradient(100deg, #edf4ff, #fff);
  box-shadow: 0 8px 22px rgba(30, 99, 223, 0.1);
}

.agent-screen {
  min-width: 0;
  overflow: hidden;
  border: 1px solid #dce5f1;
  border-radius: 10px;
  background: #f7fafc;
}

.screen-toolbar {
  height: 46px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 7px;
  box-sizing: border-box;
  border-bottom: 1px solid #e1e7ef;
  background: #fff;
}

.screen-toolbar span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #c8d4e5;
}

.screen-toolbar strong {
  margin-left: 8px;
  color: #607089;
  font-size: 12px;
  font-weight: 500;
}

.agent-screen img {
  width: 100%;
  height: calc(100% - 46px);
  min-height: 440px;
  display: block;
  object-fit: contain;
  object-position: center;
}

.screen-fade-enter-active,
.screen-fade-leave-active {
  transition: opacity 0.18s;
}

.screen-fade-enter,
.screen-fade-leave-to {
  opacity: 0;
}

.cycle-flow {
  margin-top: 28px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  border: 1px solid #dce6f3;
  border-radius: 10px;
  background: #fff;
}

.cycle-flow article {
  min-height: 112px;
  padding: 22px 18px;
  position: relative;
  box-sizing: border-box;
  text-align: center;
}

.cycle-flow article:not(:last-child)::after {
  content: "";
  width: 30px;
  height: 1px;
  position: absolute;
  top: 50%;
  right: -15px;
  z-index: 2;
  background: #8fb4f5;
}

.cycle-flow article > span,
.cycle-flow article > strong,
.cycle-flow article > small {
  display: block;
}

.cycle-flow article > span {
  margin-bottom: 8px;
  color: #98baf2;
  font-size: 11px;
}

.cycle-flow article > strong {
  margin-bottom: 7px;
  font-size: 16px;
  font-weight: 600;
}

.cycle-flow article > small {
  color: #8591a3;
  font-size: 12px;
}

.platform-section {
  background: #fff;
}

.platform-card {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(380px, 0.7fr);
  overflow: hidden;
  border: 1px solid #dce5f2;
  border-radius: 12px;
  background: #f8fbff;
  box-shadow: 0 18px 46px rgba(18, 62, 126, 0.1);
}

.platform-visual {
  min-height: 520px;
  position: relative;
  overflow: hidden;
  background: #071c2f;
}

.platform-visual img {
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;
  display: block;
  object-fit: cover;
  object-position: center;
}

.platform-visual::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 70%, rgba(7, 28, 47, 0.42));
  pointer-events: none;
}

.platform-visual > span {
  padding: 10px 16px;
  position: absolute;
  bottom: 22px;
  left: 22px;
  z-index: 2;
  color: #fff;
  border: 1px solid rgba(136, 199, 255, 0.42);
  border-radius: 5px;
  background: rgba(4, 28, 52, 0.75);
  backdrop-filter: blur(6px);
  font-size: 13px;
}

.platform-copy {
  padding: 42px 36px;
  background: #fff;
}

.platform-copy h2,
.research-summary h2,
.automation-copy h2,
.ai-application-card h2 {
  margin: 12px 0 15px;
  color: #14366b;
  font-size: 27px;
  font-weight: 600;
  line-height: 1.42;
}

.platform-copy > p,
.research-summary p,
.automation-copy > p,
.ai-application-card > div:first-child > p {
  color: #66758b;
  font-size: 14px;
  line-height: 1.85;
}

.system-chip-list {
  margin: 24px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.system-chip-list span {
  padding: 8px 12px;
  color: #245cae;
  border: 1px solid #d1e0f6;
  border-radius: 5px;
  background: #f2f7ff;
  font-size: 12px;
}

.platform-ability-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.platform-ability-grid > div {
  min-height: 56px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  border: 1px solid #e2e9f2;
  border-radius: 7px;
}

.platform-ability-grid span {
  width: 28px;
  height: 28px;
  flex: none;
  color: #2164e8;
}

.platform-ability-grid strong {
  font-size: 13px;
  font-weight: 500;
}

.research-section {
  background: #f6f9fe;
}

.research-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}

.research-grid article {
  overflow: hidden;
  border: 1px solid #e0e8f3;
  border-radius: 11px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(22, 65, 126, 0.07);
}

.research-image {
  height: 230px;
  overflow: hidden;
  background: #f5f8fc;
}

.research-image img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: top center;
  transition: transform 0.35s;
}

.research-grid article:hover .research-image img {
  transform: scale(1.025);
}

.research-copy {
  padding: 22px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.research-copy > span {
  width: 42px;
  height: 42px;
  padding: 8px;
  flex: none;
  box-sizing: border-box;
  color: #2164e8;
  border-radius: 8px;
  background: #edf4ff;
}

.research-copy h3 {
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: 600;
}

.research-copy p {
  color: #778397;
  font-size: 12px;
  line-height: 1.65;
}

.research-summary {
  margin-top: 24px;
  padding: 30px 34px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  border: 1px solid #dce7f5;
  border-radius: 10px;
  background: linear-gradient(105deg, #fff, #eef5ff);
}

.research-summary > div:first-child {
  max-width: 900px;
}

.service-scenes {
  min-width: 310px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.service-scenes span {
  padding: 11px 14px;
  color: #2461ba;
  border: 1px solid #cbdcf5;
  border-radius: 6px;
  background: #fff;
  text-align: center;
  font-size: 12px;
}

.automation-section {
  background: #fff;
}

.automation-card {
  padding: 34px;
  display: grid;
  grid-template-columns: minmax(360px, 0.72fr) minmax(0, 1.28fr);
  align-items: center;
  gap: 36px;
  border: 1px solid #dce6f3;
  border-radius: 12px;
  background: linear-gradient(115deg, #f7faff, #fff);
  box-shadow: 0 16px 42px rgba(22, 65, 126, 0.08);
}

.metric-grid {
  margin-top: 28px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.metric-grid article {
  min-height: 94px;
  padding: 18px;
  box-sizing: border-box;
  border: 1px solid #dce7f5;
  border-radius: 8px;
  background: #fff;
}

.metric-grid strong,
.metric-grid span {
  display: block;
}

.metric-grid strong {
  margin-bottom: 8px;
  color: #1764e8;
  font-size: 28px;
  font-weight: 600;
}

.metric-grid span {
  color: #6d7b90;
  font-size: 12px;
}

.metric-note {
  margin-top: 14px;
  display: block;
  color: #98a2b1;
  font-size: 11px;
  line-height: 1.5;
}

.automation-visual {
  overflow: hidden;
  border: 1px solid #d5e0ee;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(18, 62, 126, 0.1);
}

.automation-visual img {
  width: 100%;
  display: block;
}

.ai-section {
  color: #fff;
  background:
    radial-gradient(circle at 85% 20%, rgba(37, 137, 255, 0.26), transparent 30%),
    linear-gradient(135deg, #071d45, #0b3e91 62%, #0c5ac8);
}

.ai-section /deep/ .title-small-text {
  color: rgba(255, 255, 255, 0.7);
}

.ai-tech-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.ai-tech-grid article {
  min-height: 130px;
  padding: 26px;
  display: flex;
  align-items: center;
  gap: 18px;
  box-sizing: border-box;
  border: 1px solid rgba(145, 196, 255, 0.24);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
}

.ai-tech-grid article > span {
  width: 50px;
  height: 50px;
  padding: 10px;
  flex: none;
  box-sizing: border-box;
  color: #8ed1ff;
  border-radius: 9px;
  background: rgba(70, 159, 255, 0.16);
}

.ai-tech-grid h3 {
  margin-bottom: 9px;
  font-size: 19px;
  font-weight: 600;
}

.ai-tech-grid p {
  color: rgba(255, 255, 255, 0.64);
  font-size: 12px;
  line-height: 1.6;
}

.ai-application-card {
  margin-top: 24px;
  padding: 34px;
  display: grid;
  grid-template-columns: minmax(360px, 0.8fr) minmax(0, 1.2fr);
  align-items: center;
  gap: 46px;
  border: 1px solid rgba(145, 196, 255, 0.22);
  border-radius: 11px;
  background: rgba(2, 23, 61, 0.35);
}

.ai-application-card .section-kicker {
  color: #73c7ff;
}

.ai-application-card h2 {
  color: #fff;
}

.ai-application-card > div:first-child > p {
  color: rgba(255, 255, 255, 0.66);
}

.ai-scenes {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.ai-scenes article {
  min-height: 142px;
  padding: 20px;
  box-sizing: border-box;
  border: 1px solid rgba(155, 200, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.07);
}

.ai-scenes article > span {
  width: 38px;
  height: 38px;
  margin-bottom: 14px;
  display: block;
  color: #8ed1ff;
}

.ai-scenes strong,
.ai-scenes small {
  display: block;
}

.ai-scenes strong {
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 600;
}

.ai-scenes small {
  color: rgba(255, 255, 255, 0.58);
  font-size: 11px;
}

.value-section {
  background: #f6f9fe;
}

.value-grid {
  padding: 28px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  border: 1px solid #dfe8f3;
  border-radius: 11px;
  background: #fff;
}

.value-grid article {
  min-height: 128px;
  padding: 22px 28px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  box-sizing: border-box;
  border-right: 1px solid #e4eaf2;
  border-bottom: 1px solid #e4eaf2;
}

.value-grid article:nth-child(3n) {
  border-right: 0;
}

.value-grid article:nth-last-child(-n + 3) {
  border-bottom: 0;
}

.value-grid article > span {
  width: 42px;
  height: 42px;
  flex: none;
  color: #2164e8;
}

.value-grid h3 {
  margin-bottom: 9px;
  color: #17396e;
  font-size: 17px;
  font-weight: 600;
}

.value-grid p {
  color: #7a8698;
  font-size: 12px;
  line-height: 1.65;
}

.contact-banner {
  min-height: 240px;
  padding: 54px clamp(32px, 8.33vw, 160px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  box-sizing: border-box;
  color: #fff;
  background:
    radial-gradient(circle at 80% 20%, rgba(62, 160, 255, 0.28), transparent 30%),
    linear-gradient(105deg, #073384, #0d62d5);
}

.contact-banner h2 {
  margin-bottom: 15px;
  font-size: 33px;
  font-weight: 500;
}

.contact-banner p {
  color: rgba(255, 255, 255, 0.76);
  font-size: 16px;
}

.contact-banner a {
  min-width: 235px;
  padding: 16px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #1f63ea;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(0, 35, 96, 0.22);
  text-decoration: none;
}

.contact-banner a span {
  margin-bottom: 6px;
  font-size: 14px;
}

.contact-banner a strong {
  font-size: 21px;
  font-weight: 500;
}

@media (max-width: 1450px) {
  .overview-lead {
    grid-template-columns: 1fr;
  }

  .system-tags {
    justify-content: flex-start;
  }

  .product-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .platform-card {
    grid-template-columns: 1.25fr 0.75fr;
  }

  .platform-visual {
    min-height: 470px;
  }
}

@media (max-width: 1100px) {
  .agent-method {
    flex-wrap: wrap;
  }

  .agent-showcase,
  .platform-card,
  .automation-card,
  .ai-application-card {
    grid-template-columns: 1fr;
  }

  .agent-tabs {
    grid-template-columns: repeat(2, 1fr);
  }

  .cycle-flow {
    grid-template-columns: repeat(3, 1fr);
  }

  .cycle-flow article::after {
    display: none;
  }

  .platform-visual {
    min-height: 440px;
  }

  .research-grid,
  .ai-tech-grid {
    grid-template-columns: 1fr;
  }

  .research-grid article {
    display: grid;
    grid-template-columns: minmax(300px, 0.8fr) 1fr;
  }

  .research-image {
    height: 220px;
  }

  .research-summary {
    align-items: flex-start;
    flex-direction: column;
  }

  .service-scenes {
    width: 100%;
  }

  .ai-scenes,
  .value-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .value-grid article,
  .value-grid article:nth-child(3n),
  .value-grid article:nth-last-child(-n + 3) {
    border-right: 1px solid #e4eaf2;
    border-bottom: 1px solid #e4eaf2;
  }

  .value-grid article:nth-child(2n) {
    border-right: 0;
  }

  .value-grid article:nth-last-child(-n + 2) {
    border-bottom: 0;
  }
}

@media (max-width: 760px) {
  .common-section {
    padding: 70px 22px;
  }

  .smart-grid-page /deep/ .title-big-text {
    margin-bottom: 18px;
    font-size: 30px;
  }

  .smart-grid-page /deep/ .title-small-text {
    margin-bottom: 28px;
    font-size: 14px;
    line-height: 1.7;
  }

  .overview-lead {
    padding: 26px 22px;
    gap: 28px;
  }

  .overview-statement h2,
  .platform-copy h2,
  .research-summary h2,
  .automation-copy h2,
  .ai-application-card h2 {
    font-size: 23px;
  }

  .system-tags span {
    min-width: auto;
    padding: 9px 13px;
  }

  .product-grid,
  .agent-tabs,
  .cycle-flow,
  .platform-ability-grid,
  .metric-grid,
  .ai-scenes,
  .value-grid {
    grid-template-columns: 1fr;
  }

  .product-card {
    min-height: 220px;
  }

  .agent-method {
    gap: 9px;
  }

  .agent-method > span {
    display: none;
  }

  .method-pill {
    min-width: calc(50% - 8px);
    padding: 11px 15px;
    font-size: 14px;
  }

  .agent-showcase,
  .automation-card,
  .ai-application-card {
    padding: 16px;
  }

  .agent-screen img {
    min-height: 250px;
  }

  .cycle-flow article {
    min-height: 96px;
    border-bottom: 1px solid #e4eaf2;
  }

  .cycle-flow article:last-child {
    border-bottom: 0;
  }

  .platform-visual {
    min-height: 290px;
  }

  .platform-copy {
    padding: 28px 22px;
  }

  .research-grid article {
    display: block;
  }

  .research-summary {
    padding: 26px 22px;
  }

  .service-scenes {
    min-width: 0;
    grid-template-columns: 1fr;
  }

  .automation-card {
    gap: 24px;
  }

  .ai-tech-grid article {
    padding: 22px;
  }

  .value-grid {
    padding: 12px;
  }

  .value-grid article,
  .value-grid article:nth-child(2n),
  .value-grid article:nth-last-child(-n + 2) {
    border-right: 0;
    border-bottom: 1px solid #e4eaf2;
  }

  .value-grid article:last-child {
    border-bottom: 0;
  }

  .contact-banner {
    padding: 46px 22px;
    align-items: flex-start;
    flex-direction: column;
  }

  .contact-banner h2 {
    font-size: 27px;
    line-height: 1.4;
  }

  .contact-banner p {
    font-size: 14px;
    line-height: 1.7;
  }

  .contact-banner a {
    width: 100%;
    box-sizing: border-box;
  }
}
</style>
